import React, { useEffect } from 'react';
import { SEO } from '../components/SEO';
import { SignUp } from '@clerk/react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { PublicHeader } from '../components/shell/PublicHeader';
import { PublicFooter } from '../components/shell/PublicFooter';
import { useCurrentUser } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { SHOULD_USE_MOCKS } from '../lib/clerk';
import { Users, ShieldCheck, Trophy, UserPlus, Gift, CheckCircle2 } from 'lucide-react';
import { useValidateReferral } from '../hooks/useReferrals';
import { cn } from '../lib/utils';
import { getPendingToolDraft } from '../lib/toolState';
import { TOOL_EVENTS, track } from '../lib/analytics';

const appearance = {
  elements: {
    formButtonPrimary: 'bg-navy-900 hover:bg-navy-800 text-sm normal-case dark:bg-brand-primary dark:text-white',
    card: 'shadow-none border-none bg-transparent',
    headerTitle: 'text-navy-900 dark:text-white font-display',
    headerSubtitle: 'text-gray-500 dark:text-gray-400',
    socialButtonsBlockButton: 'border-gray-300 hover:bg-gray-50 text-gray-700 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:hover:bg-slate-700',
    socialButtonsBlockButtonText: 'dark:text-white font-medium',
    dividerText: 'dark:text-gray-400',
    dividerLine: 'dark:bg-slate-700',
    formFieldLabel: 'dark:text-gray-300',
    formFieldInput: 'dark:bg-slate-800 dark:border-slate-700 dark:text-white',
    footerActionText: 'dark:text-gray-400',
    footerActionLink: 'text-blue-600 hover:text-blue-700 font-medium dark:text-blue-400 dark:hover:text-blue-300',
    identityPreviewText: 'dark:text-white',
    identityPreviewEditButton: 'dark:text-blue-400 hover:dark:text-blue-300',
  },
};

export const SignUpPage = () => {
  const { isSignedIn, isLoading: isAuthLoading, loginAs, user, isMaintenanceMode } = useCurrentUser();
  const { activeBrandId, brand } = useTenant();
  const theme = activeBrandId;
  const brandName = 'OrgSets';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');
  const source = searchParams.get('source');
  const toolSlug = searchParams.get('slug');
  const orgSlugParam = searchParams.get('slug');
  const orgNameParam = searchParams.get('name') || searchParams.get('org');
  const emailParam = searchParams.get('email');
  const rawRedirect = searchParams.get('redirect_url');
  const redirectUrl = (rawRedirect && rawRedirect.startsWith('/') && !rawRedirect.startsWith('//')) ? rawRedirect : undefined;

  const pendingHub = React.useMemo(() => {
    if (typeof localStorage !== 'undefined') {
      try {
        const raw = localStorage.getItem('pending_hub_creation');
        if (raw) return JSON.parse(raw);
      } catch {
        /* ignore */
      }
    }
    if (orgSlugParam && source !== 'tool') {
      const fallbackData = {
        name: orgNameParam || orgSlugParam.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        slug: orgSlugParam,
        template: 'CLUB',
        visibility: 'PUBLIC',
        tier: 'LOCAL',
        modules: ['ROSTER', 'DUES', 'VAULT', 'PULSE']
      };
      try {
        localStorage.setItem('pending_hub_creation', JSON.stringify(fallbackData));
      } catch {
        /* ignore */
      }
      return fallbackData;
    }
    return null;
  }, [orgSlugParam, orgNameParam, source]);

  const pendingTool = React.useMemo(() => getPendingToolDraft(), []);

  const prefillEmail = React.useMemo(() => {
    if (emailParam) return emailParam;
    if (typeof localStorage === 'undefined') return '';
    try { return localStorage.getItem('orgsets_lead_email') || ''; } catch { return ''; }
  }, [emailParam]);

  const prefillName = React.useMemo(() => {
    if (typeof localStorage === 'undefined') return '';
    try { return localStorage.getItem('orgsets_lead_name') || ''; } catch { return ''; }
  }, []);

  const prefillNameParts = prefillName.trim().split(/\s+/);
  const prefillFirstName = prefillNameParts[0] || '';
  const prefillLastName = prefillNameParts.slice(1).join(' ');

  const hasTrackedStartRef = React.useRef(false);
  const hasTrackedCompleteRef = React.useRef(false);

  useEffect(() => {
    if (emailParam && typeof localStorage !== 'undefined') {
      try { localStorage.setItem('orgsets_lead_email', emailParam); } catch { /* ignore */ }
    }
    if (source === 'tool' && !hasTrackedStartRef.current) {
      hasTrackedStartRef.current = true;
      track(TOOL_EVENTS.signupStart, {
        source,
        tool_slug: toolSlug || pendingTool?.toolSlug,
        has_draft: Boolean(pendingTool?.hasState),
      });
    }
  }, [emailParam, source, toolSlug, pendingTool]);

  useEffect(() => {
    console.log(`[SignUpPage] Render state: isAuthLoading=${isAuthLoading}, isSignedIn=${isSignedIn}, hasUser=${!!user}`);
  }, [isAuthLoading, isSignedIn, user]);

  const { data: referral } = useValidateReferral(refCode || '');

  useEffect(() => {
    if (refCode) {
      sessionStorage.setItem('referralCode', refCode);
    }
  }, [refCode]);

  useEffect(() => {
    // Avoid redirects during prerendering as it destroys the execution context
    const isPrerendering = (window as any).__PRERENDER_INJECTED === true || 
                          navigator.userAgent.includes('HeadlessChrome');

    if (!isAuthLoading && isSignedIn && !isPrerendering) {
      if ((source === 'tool' || pendingTool) && !hasTrackedCompleteRef.current) {
        hasTrackedCompleteRef.current = true;
        track(TOOL_EVENTS.signupComplete, {
          source: source || 'tool',
          tool_slug: toolSlug || pendingTool?.toolSlug,
          has_draft: Boolean(pendingTool?.hasState),
        });
      }

      if (redirectUrl) {
        navigate(redirectUrl);
      } else if (pendingHub || pendingTool || source === 'tool') {
        navigate('/dashboard');
      } else {
        navigate('/create-org');
      }
    }
  }, [isAuthLoading, isSignedIn, navigate, redirectUrl, pendingHub, pendingTool, source, toolSlug]);

  const handleMockLogin = () => {
    loginAs({
      id: 'user-james',
      firstName: 'James',
      lastName: 'Avery',
      fullName: 'James Avery',
      primaryEmailAddress: { emailAddress: 'james@orgsets.com' },
      imageUrl: 'https://picsum.photos/seed/james/100/100',
      publicMetadata: { role: 'PRESIDENT' },
      username: 'james'
    });
  };

  return (
    <div className="min-h-screen flex flex-col font-sans transition-colors duration-300 bg-white dark:bg-black text-black dark:text-white">
      <SEO title="Sign Up" description="Create your organization and get started." noindex={true} />
      <PublicHeader />
      
      <main className="flex-1 flex flex-col items-center justify-center p-4 pt-28 pb-24">
        <div className={cn(
          "w-full max-w-6xl min-h-[600px] lg:min-h-[680px] overflow-hidden flex flex-col lg:flex-row",
          "bg-white border-8 border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] rounded-none dark:bg-black"
        )}>
      {/* Left Side: Marketing/Friendly Info */}
      <div className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative overflow-hidden bg-black text-white dark:bg-white dark:text-black border-r-8 border-black dark:border-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-brand-primary opacity-10 blur-3xl -mr-32 -mt-32 rounded-full"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500 opacity-10 blur-3xl -ml-48 -mb-48 rounded-full"></div>
        
        <div className="relative z-10">
          <Link to="/" className="inline-flex items-center gap-3 mb-20 group">
            {brand.logoUrl ? (
              <img src={brand.logoUrl} alt={brandName} className="h-8 w-8 object-contain bg-slate-900/5 dark:bg-white/90 p-0.5 rounded-sm" referrerPolicy="no-referrer" />
            ) : (
              <div className="p-2 rounded-lg group-hover:bg-blue-400/20 transition-colors bg-white/10 dark:bg-black/10">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
            )}
            <span className="text-2xl font-black font-display uppercase tracking-tighter">{brandName}</span>
          </Link>
          
          <h2 className="text-5xl font-black font-display tracking-tighter leading-tight mb-8 break-words !text-inherit">
            Your community <br />
            <span className="text-blue-400 italic">needs a home.</span>
          </h2>
          
          <div className="space-y-8 max-w-sm">
            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center border bg-white/10 border-white/10 dark:bg-black/10 dark:border-black/10">
                <ShieldCheck className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h4 className="font-bold mb-1 !text-inherit">Private & Secure</h4>
                <p className="opacity-60 text-sm leading-relaxed !text-inherit">No ads, no algorithms. Just a quiet, safe space for your people to connect.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center border bg-white/10 border-white/10 dark:bg-black/10 dark:border-black/10">
                <Trophy className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h4 className="font-bold mb-1 !text-inherit">Built to Grow</h4>
                <p className="opacity-60 text-sm leading-relaxed !text-inherit">Whether you're 5 friends or 5,000 members, we give you the tools to thrive.</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="relative z-10 pt-20">
          <p className="opacity-40 text-xs font-medium tracking-widest uppercase !text-inherit">Trusted by communities worldwide</p>
        </div>
      </div>

      {/* Right Side: Sign Up Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="mb-10 lg:hidden">
            <Link to="/" className="inline-flex items-center gap-3 mb-6">
              {brand.logoUrl ? (
                <img src={brand.logoUrl} alt={brandName} className="h-8 w-8 object-contain bg-slate-900/5 dark:bg-white/90 p-0.5 rounded-sm" referrerPolicy="no-referrer" />
              ) : (
                <Users className="w-6 h-6 text-brand-primary" />
              )}
              <span className="text-xl font-bold font-display uppercase tracking-tight text-navy-900 dark:text-white">{brandName}</span>
            </Link>
            <h1 className="text-3xl font-black font-display text-navy-900 dark:text-white tracking-tight mb-2">Create your account</h1>
            <p className="text-gray-500">Join {brandName} and start building your organization.</p>
          </div>
          
          {pendingTool && source === 'tool' && (
            <div className="mb-6 bg-[#FFD600] text-black p-5 border-[4px] border-black dark:border-white shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff]">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 size={14} className="text-black" />
                <span className="text-[11px] font-mono font-black uppercase tracking-wider">
                  Your draft is saved
                </span>
              </div>
              <div className="font-mono text-base font-black truncate">
                {pendingTool.toolTitle}
              </div>
              <p className="text-xs font-bold mt-1 text-black/75">
                Create your account and we will take you straight to your dashboard, with a link back to these inputs.
              </p>
            </div>
          )}

          {pendingHub && (
            <div className="mb-6 bg-[#0044FF] text-white p-5 border-[4px] border-black dark:border-white shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff]">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-[11px] font-mono font-black text-[#FFD600] uppercase tracking-wider flex items-center gap-1.5">
                  <CheckCircle2 size={14} className="text-[#FFD600]" /> URL RESERVED FOR YOU
                </span>
                <span className="bg-black text-white text-[10px] font-mono font-bold px-2 py-0.5 uppercase tracking-wider border border-white/20">
                  Primary Admin
                </span>
              </div>
              <div className="font-mono text-lg font-black truncate">
                orgsets.com/{pendingHub.slug}
              </div>
              <p className="text-xs text-white/90 font-medium mt-1">
                Create your account below to claim ownership and launch <strong>{pendingHub.name}</strong>.
              </p>
            </div>
          )}

          <div className="overflow-hidden bg-white dark:bg-black border-[4px] border-black dark:border-white shadow-[8px_8px_0px_#0044FF] dark:shadow-[8px_8px_0px_#FFD600]">
            {referral && (
              <div className="bg-brand-primary/10 px-8 py-3 flex items-center gap-3 border-b border-brand-primary/10">
                <Gift className="w-5 h-5 text-brand-primary" />
                <p className="text-xs font-semibold text-brand-primary">
                  Referral code <span className="font-mono font-bold uppercase">{referral.referralCode}</span> active
                </p>
              </div>
            )}
            <div className="p-2">
              {SHOULD_USE_MOCKS ? (
                (typeof window !== 'undefined' && !!(window as any).__PRERENDER_INJECTED?.isPrerendering) ? (
                  <div className="p-16 flex flex-col items-center justify-center text-center space-y-4">
                    <div className="w-12 h-12 border-4 border-black dark:border-white border-t-[#0044FF] rounded-full animate-spin mx-auto" />
                    <p className="font-bold text-gray-500">Preparing secure environment...</p>
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <div className="flex flex-col items-center gap-6">
                      <div className="w-16 h-16 bg-brand-primary/10 rounded-2xl flex items-center justify-center">
                        <UserPlus className="w-8 h-8 text-brand-primary" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-navy-900 dark:text-white mb-2">Instant Demo Entry</h3>
                        <p className="text-gray-500 text-sm mb-6">
                          No real account needed for the preview. You'll be logged in as a simulated leader.
                        </p>
                      </div>
                      <button 
                        onClick={handleMockLogin}
                        className="w-full py-4 font-bold transition-all active:scale-[0.98] bg-black text-white dark:bg-white dark:text-black rounded-none border-[4px] border-black dark:border-white shadow-[8px_8px_0px_#0044FF] dark:shadow-[8px_8px_0px_#FFD600] hover:shadow-[4px_4px_0px_#0044FF] hover:translate-x-1 hover:translate-y-1 font-black uppercase tracking-wider"
                      >
                        Continue as Admin
                      </button>
                      <p className="text-[10px] text-gray-400 mt-4">
                        Live authentication is disabled (No Key Found).
                      </p>
                    </div>
                  </div>
                )
              ) : (
                <div className="flex flex-col gap-4">
                  {/* Always keep SignUp mounted to avoid disrupting Clerk flows! */}
                  <div className={isAuthLoading ? "hidden" : "block"}>
                    <SignUp 
                      routing="path"
                      path="/sign-up"
                      signInUrl={redirectUrl ? `/sign-in?redirect_url=${encodeURIComponent(redirectUrl)}` : "/sign-in"}
                      fallbackRedirectUrl={redirectUrl || "/dashboard"}
                      initialValues={{
                        emailAddress: prefillEmail || undefined,
                        firstName: prefillFirstName || undefined,
                        lastName: prefillLastName || undefined,
                      }}
                      appearance={appearance}
                    />
                  </div>
                  {isAuthLoading && (
                    <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
                      <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent flex items-center justify-center animate-spin rounded-full mb-6"></div>
                      <p className="font-bold text-navy-900 dark:text-white text-lg">Synchronizing workspace...</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                        {isMaintenanceMode 
                          ? "System maintenance may cause delays" 
                          : "Preparing your dashboard. This should only take a moment."}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="h-24 flex flex-col justify-center mt-4 text-center px-8">
            <p className="text-xs text-gray-400 leading-relaxed">
              By joining, you agree to our <Link to="/terms" className="text-brand-primary hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-brand-primary hover:underline">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
};
